import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Select, Tooltip, Typography } from 'antd';

import FormulaInput from './FormulaInput';
import { describeFunctions } from '../../utils/formula';

/**
 * THE PLATFORM'S FORMULA EDITOR — the one a calculated element is written in
 * (cards/FormulaModal) and the one a chart's calculations are written in
 * (chartBuilder/MeasureStep, charts A8). One editor, so a formula reads and
 * types the same wherever keepr asks for one.
 *
 * The editing surface is FormulaInput, the Lexical editor (KPR-539; it was
 * TokenInput until then): a reference is a pill (`{{volume-a}}` is
 * unambiguous where `volume-a + …` would read as four operands), operators
 * and functions are typed, and **Insert a function…** drops `round()` at the
 * caret with the caret parked inside the parens. The caller decides what the
 * pills are (`tokens`): a card's elements for a calculated element or a
 * per-item calculation, the chart's other measures by their labels for a
 * formula across measures. The stored value is the plain formula text.
 *
 * Analysis is the caller's (it knows what the references mean); this is the
 * editing surface only.
 *
 *   value, onChange    the formula text
 *   tokens             the pill vocabulary (flat or a tree, as TokenInput takes)
 *   ariaLabel          the editable region's accessible name
 *   pickerId           the function picker's id (unique on the page)
 *   hint               a line beside the picker
 *   describedBy        the id of the messages under the field
 *   invalid            a refusal stands under it
 *
 * A ref exposes `focus()` and `insertText(text, { caretBack })`.
 */

/** Every function the module knows, grouped as its reference groups them, for the picker. */
export const functionOptions = () => {
	const groups = new Map();
	describeFunctions().forEach((fn) => {
		if (!groups.has(fn.group)) groups.set(fn.group, []);
		groups.get(fn.group).push({
			value: fn.name,
			label: `${fn.name}(${fn.args.join(', ')})`,
			searchText: `${fn.name} ${fn.description}`.toLowerCase(),
			description: fn.description,
		});
	});
	return [...groups.entries()].map(([group, options]) => ({ label: group, options }));
};

const OPERATORS = '+  −  ×  ÷  ^  %  ( )  &  =  !=  <  <=  >  >=  and  or  not';

/** The operators and every function with what it does — the body of a "Formula reference" disclosure. */
export function FormulaReference() {
	const options = useMemo(functionOptions, []);
	return (
		<div className='formula-modal__reference'>
			<Typography.Text strong>Operators</Typography.Text>
			<div className='formula-modal__operators'>{OPERATORS}</div>
			{options.map((group) => (
				<div key={group.label} className='formula-modal__reference-group'>
					<Typography.Text strong>{group.label}</Typography.Text>
					<dl className='formula-modal__reference-list'>
						{group.options.map((fn) => (
							<div key={fn.value} className='formula-modal__reference-row'>
								<dt><code>{fn.label}</code></dt>
								<dd>{fn.description}</dd>
							</div>
						))}
					</dl>
				</div>
			))}
		</div>
	);
}

const FormulaEditor = forwardRef(function FormulaEditor({
	value,
	onChange,
	tokens,
	ariaLabel = 'Formula',
	placeholder = '',
	multiline = false,
	pickerId = 'formulaFunctionPicker',
	hint = null,
	describedBy,
	invalid = false,
	disabled = false,
}, ref) {
	const editorRef = useRef(null);
	const [pickerValue, setPickerValue] = useState(undefined);
	const options = useMemo(functionOptions, []);

	useImperativeHandle(ref, () => ({
		focus: () => editorRef.current?.focus(),
		insertText: (text, opts) => editorRef.current?.insertText(text, opts),
	}), []);

	// A pick lands one timer turn after the click (KPR-1217). On a mousedown in
	// its list the Select queues a `setTimeout(0)` that hands focus back to its
	// own search box. A mouse click is slow enough for that timer to run before
	// the click does; a touch tap (mousedown, mouseup and click in one burst on
	// a touch screen with a desktop user agent) is not, so the editor took focus
	// and the Select took it straight back, and the next keystrokes went into
	// the picker. Timers of equal delay run in the order they were queued, so
	// ours runs after the Select's, and nothing takes focus after the editor.
	const pendingPicks = useRef(new Set());
	useEffect(() => {
		const pending = pendingPicks.current;
		return () => {
			pending.forEach(clearTimeout);
			pending.clear();
		};
	}, []);

	const insertFunction = useCallback((name) => {
		if (!name) return;
		const fn = describeFunctions().find((entry) => entry.name === name);
		// The caret lands inside the parens when there is something to type there.
		const caretBack = fn && fn.args.length === 0 ? 0 : 1;
		const timer = setTimeout(() => {
			pendingPicks.current.delete(timer);
			editorRef.current?.insertText(`${name}()`, { caretBack });
		}, 0);
		pendingPicks.current.add(timer);
		setPickerValue(undefined);
	}, []);

	return (
		<div className='formula-editor'>
			<FormulaInput
				ref={editorRef}
				value={value}
				onChange={onChange}
				tokens={tokens}
				ariaLabel={ariaLabel}
				placeholder={placeholder}
				multiline={multiline}
				describedBy={describedBy}
				invalid={invalid}
				disabled={disabled}
			/>
			<div className='formula-modal__toolbar'>
				<Select
					id={pickerId}
					className='formula-modal__function-picker'
					showSearch
					placeholder='Insert a function…'
					value={pickerValue}
					onChange={insertFunction}
					options={options}
					optionFilterProp='searchText'
					filterOption={(input, option) => (option?.searchText || '').includes(String(input || '').toLowerCase())}
					optionRender={(option) => (
						<Tooltip title={option.data.description} placement='right'>
							<span>{option.data.label}</span>
						</Tooltip>
					)}
					popupMatchSelectWidth={false}
					// Named for its field: a page may hold several formulas.
					aria-label={`Insert a function into ${ariaLabel}`}
					disabled={disabled}
				/>
				{hint ? <Typography.Text type='secondary' className='formula-modal__hint'>{hint}</Typography.Text> : null}
			</div>
		</div>
	);
});

export default FormulaEditor;
